import React, { useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { toDateTimeLocal, uploadFileToS3 } from "../../utils/common";
import axiosUserInstance from "../../axiosUserInstance";
import { useLoader } from "../../context/LoaderContext";

export default function CountdownModal({
  show,
  onClose,
  setUserData,
  queueFormData,
  setQueueFormData,
}) {
  const { setLoading } = useLoader();
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setQueueFormData({ ...queueFormData, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQueueFormData({ ...queueFormData, fileLink: file });
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/", "video/"];

    if (!allowedTypes.some((type) => file.type.startsWith(type))) {
      toast.error("Only images and videos are allowed!");
      return;
    }
    const thumbnailType = file.type.startsWith("image/") ? "image" : "video";
    if (file) {
      setQueueFormData({ ...queueFormData, thumbnail: file, thumbnailType });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!queueFormData.title) newErrors.title = "Title is required";
    if (!queueFormData.releaseAt)
      newErrors.releaseAt = "End date/time is required";
    return newErrors;
  };

  const submitForm = async () => {
    if (!queueFormData.title || !queueFormData.releaseAt) {
      const validationErrors = validate();
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) return;
      return;
    } else {
      setErrors({});
    }
    const utcDate = new Date(queueFormData.releaseAt).toISOString();

    try {
      setLoading(true);
      let thumbnailUrl = queueFormData?.thumbnail;
      if (queueFormData?.thumbnail instanceof File) {
        thumbnailUrl = await uploadFileToS3(queueFormData.thumbnail);
      }
      let fileLink = queueFormData?.fileLink;
      if (queueFormData?.fileLink instanceof File) {
        fileLink = await uploadFileToS3(queueFormData.fileLink);
      }
      const response = await axiosUserInstance.post(
        queueFormData?._id ? `/widget/edit` : `/widget/add`,
        {
          type: "queue",
          ...queueFormData,
          releaseAt: utcDate,
          thumbnail: thumbnailUrl,
          fileLink: fileLink,
          _id: queueFormData?._id || undefined,
        }
      );
      if (response?.status === 200) {
        const data = await response.data?.data;
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
        }));
        setQueueFormData({
          title: "",
          releaseAt: "",
          description: "",
          subType: "link",
          link: "",
          fileLink: "",
          thumbnail: "",
          containerColor: "#edf492",
          textColor: "#000000",
          containerOpacity: 1,
          borderColor: "#000000",
          borderWidth: 1,
        });
        onClose();
        toast.success("Countdown created successfully.");
      } else {
        toast.error(
          response?.message || "Countdown creation failed. Please try again."
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static" className="countdown-modal">
      <Modal.Header closeButton>
        <Modal.Title className="mb-0">Queue for Release</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group>
            <label>Title</label>
            <Form.Control
              type="text"
              name="title"
              placeholder="eg. Blog Release"
              value={queueFormData.title}
              onChange={handleChange}
              isInvalid={!!errors.title}
            />
            {errors.title && (
              <div className="text-danger small">{errors.title}</div>
            )}
          </Form.Group>

          <Row className="mt-3">
            <Col>
              <label>Choose Countdown End Date</label>
              <Form.Control
                type="datetime-local"
                name="releaseAt"
                value={toDateTimeLocal(queueFormData.releaseAt || null)}
                onChange={handleChange}
              />
              {errors.releaseAt && (
                <div className="text-danger small">{errors.releaseAt}</div>
              )}
            </Col>
          </Row>

          <hr className="my-4" />
          <h5>Embed Content</h5>

          <Form.Group className="mt-4">
            <label>Description *</label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              placeholder="eg. Happy New Year! Watch our new vid here"
              value={queueFormData.description}
              onChange={handleChange}
            />
          </Form.Group>

          {/* Tabs */}
          <div className="embed-tabs mt-3">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    queueFormData?.subType === "link" ? "active" : ""
                  }`}
                  type="button"
                  onClick={() =>
                    setQueueFormData({ ...queueFormData, subType: "link" })
                  }
                >
                  + Link
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    queueFormData?.subType === "image" ? "active" : ""
                  }`}
                  type="button"
                  onClick={() =>
                    setQueueFormData({ ...queueFormData, subType: "image" })
                  }
                >
                  + Img/Vid
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    queueFormData?.subType === "files" ? "active" : ""
                  }`}
                  type="button"
                  onClick={() =>
                    setQueueFormData({ ...queueFormData, subType: "files" })
                  }
                >
                  + Files
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content p-3 border border-top-0">
              {queueFormData?.subType === "link" && (
                <>
                  <div className="mb-3">
                    <label>Link URL</label>
                    <Form.Control
                      type="text"
                      name="link"
                      placeholder="https://example.com"
                      value={queueFormData.link}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label>Upload Thumbnail</label>
                    <Form.Control
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleThumbnailChange}
                    />
                  </div>
                </>
              )}

              {queueFormData?.subType === "image" && (
                <div>
                  <label>Upload Image/Video (16:9 / 3:4 / 1:1)</label>
                  <Form.Control
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleThumbnailChange}
                  />
                </div>
              )}

              {queueFormData?.subType === "files" && (
                <>
                  <div className="mb-2">
                    <label>Upload Files</label>
                    <Form.Control type="file" onChange={handleFileChange} />
                  </div>
                  <div>
                    <label>Upload Thumbnail</label>
                    <Form.Control
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleThumbnailChange}
                    />
                  </div>
                </>
              )}
              {queueFormData?.thumbnail && typeof queueFormData?.thumbnail === "string" && queueFormData?.thumbnail?.includes('https') && (
                <div className="object-fit-content mt-3">
                  <p className="mb-1">Uploaded file preview:</p>
                  {queueFormData?.thumbnailType === "video" ? (
                    <video
                      src={queueFormData?.thumbnail}
                      alt="Released Content"
                      className="object-fit"
                      controls
                    />
                  ) : (
                    <img
                      src={queueFormData?.thumbnail}
                      alt="Released Content"
                      className="object-fit"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <hr className="my-4" />
          <h5>Styling & Engagement</h5>

          <Row className="mt-3">
            <Col>
              <label>Container Color</label>
              <Form.Control
                type="color"
                name="containerColor"
                value={queueFormData.containerColor}
                onChange={handleChange}
              />
            </Col>
            <Col>
              <label>Container Opacity</label>
              <Form.Range
                min={0}
                max={1}
                step={0.05}
                name="containerOpacity"
                value={queueFormData.containerOpacity}
                onChange={handleChange}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <label>Border Color</label>
              <Form.Control
                type="color"
                name="borderColor"
                value={queueFormData.borderColor}
                onChange={handleChange}
              />
            </Col>
            <Col>
              <label>Border Width</label>
              <Form.Range
                min={0}
                max={10}
                step={1}
                name="borderWidth"
                value={queueFormData.borderWidth}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <label>Text Color</label>
              <Form.Control
                type="color"
                name="textColor"
                value={queueFormData.textColor}
                onChange={handleChange}
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <p style={{ fontSize: "14px" }} className="mb-3 text-center">
          Allowing public the option to signup and receive email notifications
          when the countdown ends is Default in "Queue". If not intended, go to
          "countd"
        </p>
        <Button variant="primary" onClick={submitForm}>
          {queueFormData?._id ? `Update Queue` : `Create Queue`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
